import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useApiFetch from '@/hooks/useApiFetch';
import useSeoProject from '@/hooks/useSeoProject';
import { FetchApiIds } from '@/types';
import searchConsoleService, { GoogleConnectionStatus, SearchConsoleProperty } from '@/services/searchConsole';
import googleAnalyticsService, { GA4Property } from '@/services/googleAnalytics';
import seoProjectsService from '@/services/seoProjects';
import wordpressService from '@/services/wordpress';
import SEOxProjectSettingsComponent from './SEOxProjectSettings.component';

const SEOxProjectSettingsContainer = function (): JSX.Element {
  const { project, refetchProject } = useSeoProject();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSite, setSelectedSite] = useState(project?.siteUrl ?? '');
  const [selectedGa4, setSelectedGa4] = useState(project?.ga4PropertyId ?? '');
  const [projectName, setProjectName] = useState(project?.name ?? '');

  // WordPress state
  const [wpSiteUrl, setWpSiteUrl] = useState('');
  const [wpUsername, setWpUsername] = useState('');
  const [wpAppPassword, setWpAppPassword] = useState('');
  const [isConnectingWp, setIsConnectingWp] = useState(false);
  const [wpConnectionError, setWpConnectionError] = useState<string | null>(null);
  const [isSyncingWpPages, setIsSyncingWpPages] = useState(false);
  const [wpPagesSynced, setWpPagesSynced] = useState<number | null>(null);

  // DB-only connection check
  const { data: connectionStatus, isLoading: isLoadingConnection } = useSeoFetch<GoogleConnectionStatus>(
    [FetchApiIds.SEO_GOOGLE_CONNECTION],
    () => searchConsoleService.getConnectionStatus(),
    { retry: false }
  );

  const isConnected = !!connectionStatus?.isConnected;

  // Edge function calls -- only when connected
  const { data: availableSites = [], isLoading: isLoadingSites } = useSeoFetch<SearchConsoleProperty[]>(
    ['seo-available-sites'],
    () => searchConsoleService.getAvailableSites(),
    { enabled: isConnected, retry: false }
  );

  // GA4 (still returns AxiosResponse)
  const ga4Response = useApiFetch<GA4Property[], void>(
    ['seo-ga4-properties'],
    async () => googleAnalyticsService.listProperties(),
    { enabled: isConnected, retry: false }
  );
  const ga4Properties = (ga4Response.data as GA4Property[]) ?? [];
  const isLoadingGa4 = ga4Response.isLoading;

  // Sync local state when project loads
  if (project && !selectedSite && project.siteUrl) setSelectedSite(project.siteUrl);
  if (project && !selectedGa4 && project.ga4PropertyId) setSelectedGa4(project.ga4PropertyId);
  if (project && !projectName && project.name) setProjectName(project.name);

  const handleSave = useCallback(async () => {
    if (!project) return;
    setIsSaving(true);
    try {
      const ga4Prop = ga4Properties.find((p) => p.propertyId === selectedGa4);
      await seoProjectsService.update(project.id, {
        name: projectName || undefined,
        siteUrl: selectedSite || undefined,
        ga4PropertyId: selectedGa4 || undefined,
        ga4PropertyName: ga4Prop?.displayName || undefined,
      });
      refetchProject();
    } finally {
      setIsSaving(false);
    }
  }, [project, projectName, selectedSite, selectedGa4, ga4Properties, refetchProject]);

  const handleConnectWordPress = useCallback(async () => {
    if (!project) return;
    setIsConnectingWp(true);
    setWpConnectionError(null);
    try {
      await wordpressService.connect({
        projectId: project.id,
        wpSiteUrl,
        wpUsername,
        wpAppPassword,
      });
      setWpAppPassword('');
      refetchProject();
    } catch (err) {
      setWpConnectionError(err instanceof Error ? err.message : 'Connection failed');
    }
    setIsConnectingWp(false);
  }, [project, wpSiteUrl, wpUsername, wpAppPassword, refetchProject]);

  const handleDisconnectWordPress = useCallback(async () => {
    if (!project) return;
    if (!window.confirm('Disconnect WordPress? You can reconnect later.')) return;
    try {
      await wordpressService.disconnect(project.id);
      refetchProject();
    } catch { /* ignore */ }
  }, [project, refetchProject]);

  const handleSyncWpPages = useCallback(async () => {
    if (!project) return;
    setIsSyncingWpPages(true);
    setWpPagesSynced(null);
    try {
      const result = await wordpressService.syncPages(project.id);
      setWpPagesSynced(result.synced ?? result.total ?? 0);
    } catch (err) {
      setWpConnectionError(err instanceof Error ? err.message : 'Sync failed');
    }
    setIsSyncingWpPages(false);
  }, [project]);

  const handleDelete = useCallback(async () => {
    if (!project) return;
    if (!window.confirm(`Delete project "${project.name}"? This cannot be undone.`)) return;
    await seoProjectsService.delete(project.id);
    navigate('/seox');
  }, [project, navigate]);

  return (
    <SEOxProjectSettingsComponent
      project={project ?? null}
      connectionStatus={connectionStatus ?? null}
      availableSites={availableSites}
      ga4Properties={ga4Properties}
      isLoading={isLoadingConnection}
      isLoadingGa4={isLoadingGa4 && isConnected}
      isSaving={isSaving}
      projectName={projectName}
      onProjectNameChange={setProjectName}
      selectedSite={selectedSite}
      onSelectedSiteChange={setSelectedSite}
      selectedGa4={selectedGa4}
      onSelectedGa4Change={setSelectedGa4}
      onSave={handleSave}
      onDelete={handleDelete}
      wpSiteUrl={wpSiteUrl}
      onWpSiteUrlChange={setWpSiteUrl}
      wpUsername={wpUsername}
      onWpUsernameChange={setWpUsername}
      wpAppPassword={wpAppPassword}
      onWpAppPasswordChange={setWpAppPassword}
      onConnectWordPress={handleConnectWordPress}
      onDisconnectWordPress={handleDisconnectWordPress}
      isConnectingWp={isConnectingWp}
      wpConnectionError={wpConnectionError}
      onSyncWpPages={handleSyncWpPages}
      isSyncingWpPages={isSyncingWpPages}
      wpPagesSynced={wpPagesSynced}
    />
  );
};

export default SEOxProjectSettingsContainer;
